import { supabase } from "./supabaseClient";
import type { LeaderboardFilters, LeaderboardPayload } from "@/types/leaderboard";
import type { LeaderboardRow } from "@/types/database";

/**
 * Public leaderboard read. Hits the leaderboard_view directly (anon SELECT
 * grant is set in 0001_init.sql). The view never exposes student emails.
 *
 * Used by:
 *   - /leaderboard public page
 *   - /student/results "your rank" lookup (filtered by question_set_id)
 */
export async function fetchLeaderboard(filters: LeaderboardFilters = {}): Promise<LeaderboardPayload> {
  const limit = Math.min(filters.limit ?? 10, 100);

  let query = supabase
    .from("leaderboard_view")
    .select("*", { count: "exact" })
    .order("rank", { ascending: true })
    .limit(limit);

  if (filters.question_set_id) query = query.eq("question_set_id", filters.question_set_id);
  if (filters.event_id) query = query.eq("event_id", filters.event_id);
  if (filters.school_id) query = query.eq("school_id", filters.school_id);
  if (filters.city) query = query.eq("city", filters.city);

  const { data, error, count } = await query;
  if (error) throw error;

  return {
    rows: (data ?? []) as LeaderboardRow[],
    total: count ?? (data?.length ?? 0),
    fetched_at: new Date().toISOString(),
  };
}

/** Group leaderboard rows by school for the Texas map markers. */
export interface SchoolMarker {
  school_id: string;
  school_name: string;
  city: string | null;
  latitude: number;
  longitude: number;
  students: LeaderboardRow[];
}

export function groupBySchool(rows: LeaderboardRow[]): SchoolMarker[] {
  const buckets = new Map<string, SchoolMarker>();
  for (const r of rows) {
    if (r.school_id == null || r.latitude == null || r.longitude == null) continue;
    const key = r.school_id;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        school_id: r.school_id,
        school_name: r.school_name ?? "Unknown school",
        city: r.city,
        latitude: r.latitude,
        longitude: r.longitude,
        students: [],
      };
      buckets.set(key, bucket);
    }
    bucket.students.push(r);
  }
  // Sort each bucket's students by rank
  for (const b of buckets.values()) {
    b.students.sort((a, b2) => a.rank - b2.rank);
  }
  return Array.from(buckets.values()).sort(
    (a, b) => (a.students[0]?.rank ?? 999) - (b.students[0]?.rank ?? 999)
  );
}
